import React from "react";
import { ArchiveBoxIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PageI } from "../../../types";
import Button from "../../../componentLibrary/Button";

interface ArchivedPagesTabContentProps {
  pages: PageI[];
  selectedPageId: string | null;
  onSelectPage: (page: PageI) => void;
  onUnarchivePage: (pageId: string) => void;
  onDeletePage: (pageId: string, title: string) => void;
  isUnarchiving: boolean;
  isDeleting: boolean;
}

const ArchivedPagesTabContent: React.FC<ArchivedPagesTabContentProps> = ({
  pages,
  selectedPageId,
  onSelectPage,
  onUnarchivePage,
  onDeletePage,
  isUnarchiving,
  isDeleting,
}) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (pages.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">No archived pages</div>
    );
  }

  return (
    <div className="divide-y">
      {pages.map((page: PageI) => (
        <div
          key={page._id}
          className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
            selectedPageId === page._id
              ? "bg-purple-50 border-l-4 border-purple-500"
              : ""
          }`}
          onClick={() => onSelectPage(page)}
        >
          <div className="flex items-start gap-3">
            <ArchiveBoxIcon className="w-8 h-8 text-purple-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">
                {page.title || "Untitled Page"}
              </p>
              <p className="text-xs text-gray-500">
                {page.components?.length || 0} components
              </p>
              <p className="text-xs text-purple-500 mt-1">
                Archived {formatDate(page.archivedAt)}
              </p>
            </div>
            <div className="flex flex-col gap-1 flex-shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnarchivePage(page._id!);
                }}
                disabled={isUnarchiving || isDeleting}
                loading={isUnarchiving}
              >
                Unarchive
              </Button>
              <Button
                variant="cautionary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePage(page._id!, page.title || "Untitled Page");
                }}
                disabled={isUnarchiving || isDeleting}
                loading={isDeleting}
                className="flex items-center gap-1"
              >
                <TrashIcon className="w-4 h-4" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ArchivedPagesTabContent;
